import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URI = process.env.REACT_APP_API_URI || 'http://localhost:5000';
const CloudUrl = process.env.REACT_APP_CLOUD_FLARE_URL;

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

    const fetchProject = async () => {
      if (!id) {
        setError('Invalid project ID.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URI}/api/portfolio/${id}`, { headers: authHeaders });

        if (response.status !== 200) {
          throw new Error(`Error fetching project: ${response.statusText}`);
        }

        const data = response.data;

        setProject({
          ...data,
          imageUrl: data.images && data.images.length
            ? `${CloudUrl}/${data.images[0]}`
            : ''
        });
      } catch (error) {
        console.error('Error fetching project:', error);
        setError(error.response?.data?.error || 'Failed to load the project. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id, token]);

  if (loading) {
    return (
      <div className="bg-black text-white p-8 min-h-screen flex items-center justify-center">
        <p className="text-xl">Loading project details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black text-white p-8 min-h-screen flex flex-col items-center justify-center">
        <p className="text-xl mb-4">{error}</p>
        <button
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition-all duration-700"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="bg-black text-white p-8 min-h-screen flex items-center justify-center">
        <p className="text-xl">Project not found.</p>
      </div>
    );
  }

  return (
    <div className="bg-black text-white p-8 md:p-16 min-h-screen">
      {/* Hero Section */}
      <div className="p-1 md:p-5 rounded-lg mb-1 mx-1 md:mx-8">
        <h1 className="text-3xl md:text-5xl font-bold mb-6 text-center">{project.title}</h1>
        {project.images && project.images.length > 0 && (
          <img
            src={`${CloudUrl}/${project.images[0]}`}
            alt="Project Hero"
            className="w-full h-auto max-h-96 object-cover mb-1 rounded-lg"
          />
        )}
      </div>

      {/* Content Section */}
      <div className="prose prose-lg prose-invert max-w-none mx-4 md:mx-16">
        <div dangerouslySetInnerHTML={{ __html: project.content }} />
      </div>

      {/* Additional Images */}
      {project.images && project.images.slice(1).map((img, idx) => (
        <img
          key={idx}
          src={`${CloudUrl}/${img}`}
          alt={`Extra #${idx}`}
          className="mt-4 w-full max-w-screen-md object-cover rounded"
        />
      ))}

      {/* Call to Action */}
      <div className="mt-12 text-center">
        {project.githubUrl && project.githubUrl !== '#' && (
          <a
            href={project.githubUrl}
            className="text-green-500 text-lg font-semibold hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            View on GitHub
          </a>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
